import { Add, Close } from '@mui/icons-material';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useStateRef } from 'app/services';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';

const isVoykanUi = config.clientUi === 'voykan';
const checklistFieldTypeMenu = isVoykanUi ? config.checklistTemplateFormMenuVoykan : config.transportGroupCheckListFormMenu;
const inspectionModeOptions = isVoykanUi ? config.inspectionModeOptionsVoykan : config.inspectionModeOptions;
const inspectionModeHelper = isVoykanUi ? config.inspectionModeHelperVoykan : config.inspectionModeHelper;
const checkpointRequiredLabel = isVoykanUi ? config.checkpointRequiredLabelVoykan : config.checkpointRequiredLabel;
const checkpointRequiredHelper = isVoykanUi ? config.checkpointRequiredHelperVoykan : config.checkpointRequiredHelper;
const defaultInspectionMode = isVoykanUi ? 'voykan' : '';

const RenderFieldArray = ({ field, error, register, registerKey, control, firstUpdate, disableRemove, setValue, onRemove }) => (
  <React.Fragment key={field.id}>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormControl error={!!error?.title} margin='normal' fullWidth>
          <InputLabel disableAnimation={firstUpdate}>Заголовок</InputLabel>
          <Controller
            name={`${registerKey}.title`}
            defaultValue=''
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <OutlinedInput
                label='Заголовок'
                value={field.value}
                placeholder='Заголовок'
                variant='outlined'
                notched={firstUpdate}
                onChange={(value) => setValue(`${registerKey}.title`, value)}
                {...field}></OutlinedInput>
            )}></Controller>
        </FormControl>
      </Grid>
      <Grid item xs={4.5}>
        <FormControl error={!!error?.type} variant='outlined' margin='normal' fullWidth>
          <InputLabel disableAnimation={firstUpdate}>Тип</InputLabel>
          <Select
            label='Тип'
            defaultValue=''
            notched={firstUpdate}
            value={field.type}
            MenuProps={{
              sx: {
                '.MuiPaper-root:before': {
                  display: 'none',
                },
              },
            }}
            {...register(`${registerKey}.type`, {
              required: true,
              onChange: (e) => {
                const selectedValue = e.target.value;
                const selectedItem = checklistFieldTypeMenu.find((item) => item.value === selectedValue);
                if (!selectedItem) return;

                if (selectedItem.originalValue !== 'select') {
                  setValue(`${registerKey}.values`, '');
                }

                if (selectedItem.originalValue !== 'boolean') {
                  setValue(`${registerKey}.required`, 0);
                }

                field.type = selectedValue;
                setValue(`${registerKey}.type`, selectedValue);
              },
            })}>
            {checklistFieldTypeMenu.map((item, key) => (
              <MenuItem key={key} value={item.value} disableRipple>
                {item.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={1.5}>
        <Button
          variant='contained'
          size='large'
          disableElevation
          disabled={disableRemove}
          onClick={disableRemove ? null : onRemove}
          endIcon={<Close />}
          sx={(theme) => ({
            position: 'relative',
            minWidth: 'auto',
            borderRadius: theme.spacing(1),
            color: theme.palette.text.secondary,
            padding: '12px 12px',
            bgcolor: 'background.default',
            marginTop: '15px',
            marginBottom: theme.spacing(1),
            marginLeft: '3px',
            span: {
              margin: theme.spacing(0),
            },
            path: {
              fill: theme.palette.text.secondary,
            },
            '&:hover, &.active': {
              bgcolor: 'background.default',
            },
            '&.Mui-disabled': {
              bgcolor: 'background.default',
              opacity: 0.75,
            },
          })}
        />
      </Grid>
    </Grid>
    {field?.type === 'select' && (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl
            error={!!error?.values}
            margin='normal'
            variant='outlined'
            fullWidth
            sx={(theme) => ({
              width: '100%',
              marginTop: theme.spacing(2),
            })}>
            <InputLabel>Введите поля выбора</InputLabel>
            <OutlinedInput
              label='Введите поля выбора'
              placeholder='Введите поля выбора'
              width='100%'
              variant='outlined'
              {...register(`${registerKey}.values`, {
                onChange: (e) =>
                  setValue(
                    `${registerKey}.values`,
                    e.target.value.replace(
                      // eslint-disable-next-line no-useless-escape
                      /[!@#$%^&*()_+\-=\[\]{}':"\\|,.<>\/?]+/,
                      ''
                    )
                  ),
              })}></OutlinedInput>
            <FormHelperText>Если вы хотите указать несколько пунктов, то используйте указанный формат пункт 1;пункт 2</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    )}
    {field?.type === 'boolean' && (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl
              error={!!error?.required}
              margin='normal'
              variant='outlined'
              fullWidth
              sx={(theme) => ({
                width: '100%',
                marginTop: theme.spacing(2),
              })}>
              <InputLabel>{checkpointRequiredLabel}</InputLabel>
              <Select
                label={checkpointRequiredLabel}
                notched={firstUpdate}
                value={field.required}
                MenuProps={{
                  sx: {
                    '.MuiPaper-root:before': {
                      display: 'none',
                    },
                  },
                }}
                {...register(`${registerKey}.required`, {
                  onChange: (e) => {
                    field.required = e.target.value;
                    setValue(`${registerKey}.required`, e.target.value);
                  },
                })}>
                <MenuItem value={0} disableRipple>
                  Нет
                </MenuItem>
                <MenuItem value={1} disableRipple>
                  Да
                </MenuItem>
              </Select>
              <FormHelperText>{checkpointRequiredHelper}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl
              error={!!error?.required}
              margin='normal'
              variant='outlined'
              sx={(theme) => ({
                border: '1px solid',
                borderColor: theme.palette.divider,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px',
                borderRadius: '4px',
              })}>
              <Typography variant='body2'>Коментарий</Typography>
              <Switch
                edge='end'
                size='small'
                {...register(`${registerKey}.is_comment`)}
                onChange={(e) => {
                  const currentValue = e.target.checked;
                  field.is_comment = currentValue;
                  setValue(`${registerKey}.is_comment`, currentValue);
                }}
                checked={field.is_comment}
              />
            </FormControl>
            <FormHelperText>Оставлять комментарий при отрицательном выборе чекбокса</FormHelperText>
          </Grid>
          <Grid item xs={6}>
            <FormControl
              error={!!error?.required}
              margin='normal'
              variant='outlined'
              sx={(theme) => ({
                border: '1px solid',
                borderColor: theme.palette.divider,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px',
                borderRadius: '4px',
              })}>
              <Typography variant='body2'>Фото</Typography>
              <Switch
                edge='end'
                size='small'
                {...register(`${registerKey}.is_photo`)}
                onChange={(e) => {
                  const currentValue = e.target.checked;

                  field.is_photo = currentValue;
                  setValue(`${registerKey}.is_photo`, currentValue);
                }}
                checked={field.is_photo}
              />
            </FormControl>
            <FormHelperText>Добавлять фотографии при отрицательном выборе чекбокса</FormHelperText>
          </Grid>
        </Grid>
      </>
    )}
  </React.Fragment>
);

const ChecklistTemplateFieldArray = ({ index: parentIndex, onRemove: parentOnRemove, firstUpdate, disableRemove }) => {
  const { register, control, setValue, watch, formState } = useFormContext();
  const { fields, remove, append } = useFieldArray({
    fields,
    control,
    name: `checkpoints.${parentIndex}.checkpoints`,
  });

  const watchField = watch(`checkpoints.${parentIndex}.checkpoints`);
  const onRemove = (index) => (fields.length > 1 ? remove(index) : parentOnRemove(parentIndex));
  const onAppend = () =>
    append({
      type: '',
      title: '',
      values: '',
      required: 0,
    });

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item xs={10.5}>
          <FormControl error={!!formState.errors.checkpoints?.[parentIndex].title} margin='normal' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Название группы чекпоинтов</InputLabel>
            <Controller
              name={`checkpoints.${parentIndex}.title`}
              defaultValue=''
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <OutlinedInput
                  label='Название группы чекпоинтов'
                  value={field.value}
                  placeholder='Название группы чекпоинтов'
                  variant='outlined'
                  notched={firstUpdate}
                  onChange={(value) => setValue(`checkpoints.${parentIndex}.title`, value)}
                  {...field}></OutlinedInput>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            variant='contained'
            size='large'
            disableElevation
            onClick={onAppend}
            endIcon={<Add />}
            sx={(theme) => ({
              position: 'relative',
              minWidth: 'auto',
              borderRadius: theme.spacing(1),
              color: theme.palette.text.secondary,
              padding: '12px 12px',
              bgcolor: 'background.default',
              marginTop: '15px',
              marginBottom: theme.spacing(1),
              marginLeft: '3px',
              span: {
                margin: theme.spacing(0),
              },
              path: {
                fill: theme.palette.text.secondary,
              },
              '&:hover, &.active': {
                bgcolor: 'background.default',
              },
            })}
          />
        </Grid>
      </Grid>
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          <Divider
            textAlign='center'
            sx={(theme) => ({
              width: '100%',
              alignSelf: 'center',
              justifyContent: 'center',
              marginTop: theme.spacing(1.25),
              marginBottom: theme.spacing(1.25),
            })}>
            <Typography variant='body2'>
              Чекпоинт {parentIndex + 1}.{index + 1}
            </Typography>
          </Divider>
          <RenderFieldArray
            field={field}
            index={index}
            registerKey={`checkpoints.${parentIndex}.checkpoints.${index}`}
            firstUpdate={firstUpdate}
            register={register}
            control={control}
            setValue={setValue}
            onRemove={() => onRemove(index)}
            disableRemove={fields.length < 2 ? disableRemove : false}
            watchField={watchField[index]}
            error={formState.errors.checkpoints?.[parentIndex]?.checkpoints?.[index]}
          />
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

const ChecklistTemplateForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const { defaultValues, onSubmit } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const form = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      active: defaultValues ? defaultValues.active : 1,
      group_id: defaultValues ? defaultValues.group_id : null,
      inspection_mode: defaultValues ? (defaultValues.inspection_mode ?? '') : defaultInspectionMode,
      checkpoints: defaultValues
        ? defaultValues.checkpoints
        : [
            {
              type: '',
              title: '',
              values: '',
              required: 0,
              is_comment: false,
              is_photo: false,
            },
          ],
    },
  });

  const { watch, control, setValue, register, handleSubmit, formState, reset } = form;
  const { fields, append, remove } = useFieldArray({
    name: 'checkpoints',
    control,
  });
  const watchFieldArray = watch('checkpoints');
  const handleAppendCheckpoint = () => {
    setAnchorEl(null);
    append({
      type: '',
      title: '',
      values: '',
      required: 0,
      is_comment: false,
      is_photo: false,
    });
  };

  const handleAppendCheckpoints = () => {
    setAnchorEl(null);
    append({
      title: '',
      checkpoints: [
        {
          type: '',
          title: '',
          values: '',
          required: 0,
          is_comment: false,
          is_photo: false,
        },
      ],
    });
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) => {
        onSubmit({
          id: defaultValues ? defaultValues.id : undefined,
          name: data.name,
          active: data.active,
          group_id: data.group_id ? data.group_id : undefined,
          inspection_mode: data.inspection_mode === '' ? null : data.inspection_mode,
          checkpoints: data.checkpoints.map((checkpoint) => ({
            ...checkpoint,
            is_comment: checkpoint.is_comment ? 1 : 0,
            is_photo: checkpoint.is_photo ? 1 : 0,
            checkpoints: checkpoint.checkpoints
              ? checkpoint.checkpoints.map((subCheckpoint) => ({
                  ...subCheckpoint,
                  is_comment: subCheckpoint.is_comment ? 1 : 0,
                  is_photo: subCheckpoint.is_photo ? 1 : 0,
                }))
              : undefined,
          })),
        });
      })();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        active: defaultValues.active,
        group_id: defaultValues.group_id ? defaultValues.group_id : null,
        inspection_mode: defaultValues?.inspection_mode ?? '',
        checkpoints: defaultValues.checkpoints.map((checkpoint) => {
          if (checkpoint.length === 2) {
            const [title, checkpoints] = checkpoint;
            return {
              title,
              checkpoints: checkpoints.map(([type, title, values, required, is_comment, is_photo]) => ({
                type,
                title,
                values,
                required,
                is_comment: is_comment,
                is_photo: is_photo,
              })),
            };
          }

          const [type, title, values, required, is_comment, is_photo] = checkpoint;

          return {
            type,
            title,
            values,
            required,
            is_comment,
            is_photo,
          };
        }),
      });
    }
  }, [defaultValues]);

  return (
    <FormProvider {...form}>
      <form>
        <Grid container>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl error={!!formState.errors.name} variant='outlined' margin='normal' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>Название</InputLabel>
                <OutlinedInput
                  label='Название'
                  variant='outlined'
                  notched={firstUpdate}
                  fullWidth
                  placeholder='Название'
                  {...register('name', {
                    required: true,
                    minLength: 3,
                  })}></OutlinedInput>
                <FormHelperText>Используйте название чеклиста для отображения в списках</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl error={!!formState.errors.active} variant='outlined' margin='normal' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>Статус</InputLabel>
                <Controller
                  name='active'
                  control={control}
                  render={({ field }) => (
                    <Select
                      label='Статус'
                      notched={firstUpdate}
                      value={field.value}
                      MenuProps={{
                        sx: {
                          '.MuiPaper-root:before': {
                            display: 'none',
                          },
                        },
                      }}
                      {...register(field.name, {
                        required: true,
                      })}>
                      <MenuItem value={0}>Не активен</MenuItem>
                      <MenuItem value={1}>Активен</MenuItem>
                    </Select>
                  )}></Controller>
                <FormHelperText>Укажите статус для доступности чеклиста при проведении осмотра</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant='outlined' margin='normal' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>Режим осмотра (моб.)</InputLabel>
                <Controller
                  name='inspection_mode'
                  control={control}
                  render={({ field }) => (
                    <Select
                      label='Режим осмотра (моб.)'
                      notched={firstUpdate}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      MenuProps={{
                        sx: {
                          '.MuiPaper-root:before': {
                            display: 'none',
                          },
                        },
                      }}>
                      {inspectionModeOptions.map((item) => (
                        <MenuItem key={item.value || 'default'} value={item.value}>
                          {item.title}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{inspectionModeHelper}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          {fields.length !== 0 &&
            fields.map((field, index) => {
              // eslint-disable-next-line no-prototype-builtins
              if (field.hasOwnProperty('checkpoints')) {
                return (
                  <React.Fragment key={field.id}>
                    <Divider
                      textAlign='center'
                      sx={(theme) => ({
                        width: '100%',
                        alignSelf: 'center',
                        justifyContent: 'center',
                        marginTop: theme.spacing(1.25),
                        marginBottom: theme.spacing(1.25),
                      })}>
                      <Typography variant='body2'>Чекпоинт {index + 1}</Typography>
                    </Divider>
                    <ChecklistTemplateFieldArray
                      index={index}
                      firstUpdate={firstUpdate}
                      disableRemove={fields.length < 2}
                      onRemove={() => remove(index)}
                    />
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={field.id}>
                    <Divider
                      textAlign='center'
                      sx={(theme) => ({
                        width: '100%',
                        alignSelf: 'center',
                        justifyContent: 'center',
                        marginTop: theme.spacing(1.25),
                        marginBottom: theme.spacing(1.25),
                      })}>
                      <Typography variant='body2'>Чекпоинт {index + 1}</Typography>
                    </Divider>
                    <RenderFieldArray
                      field={field}
                      index={index}
                      registerKey={`checkpoints.${index}`}
                      firstUpdate={firstUpdate}
                      register={register}
                      control={control}
                      setValue={setValue}
                      disableRemove={fields.length < 2}
                      watchField={watchFieldArray[index]}
                      error={formState.errors.checkpoints?.[index]}
                      onRemove={() => remove(index)}
                    />
                  </React.Fragment>
                );
              }
            })}
          <Divider
            textAlign='center'
            sx={(theme) => ({
              width: '100%',
              alignSelf: 'center',
              justifyContent: 'center',
              marginTop: theme.spacing(2.25),
              marginBottom: theme.spacing(1.25),
            })}>
            <Button
              id='checkpointAdd'
              variant='outlined'
              size='medium'
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={(theme) => ({
                gap: '4px',
                padding: '3px 10px',
                borderRadius: '50px',
                fontSize: '10px',
                minWidth: '40px',
                color: theme.palette.text.secondary,
                bgcolor: 'background.default',
                borderColor: 'background.default',
                '&:hover, &.active': {
                  bgcolor: 'background.default',
                  borderColor: 'background.default',
                },
                svg: {
                  fontSize: '18px',
                },
              })}>
              <Add />
            </Button>
            <Menu
              id='checkpointAdd'
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  '&:before': {
                    display: 'none',
                  },
                },
              }}>
              <MenuItem onClick={handleAppendCheckpoint}>Чекпоинт</MenuItem>
              <MenuItem onClick={handleAppendCheckpoints}>Группу чекпоинтов</MenuItem>
            </Menu>
          </Divider>
        </Grid>
      </form>
    </FormProvider>
  );
});

export default ChecklistTemplateForm;
